import { useEffect, useState } from 'react';
import mustache from 'mustache';
import {
  ProductRecommendationsJsonItemsMapping,
  ProductRecommendationsJsonItemsMappingType,
} from '@narvar/nth-hook-loma';

import { useCustomComponentStateContext } from './useCustomComponentState';
import useCertonaApi from './useCertonaApi';
import useJsonApi from './useJsonApi';
import useMLProductRecs, {
  MLProductRecsItemsMap,
  MLProductRecsItemsLookup,
} from './useMLProductRecs';

export type MappedItem =
  | Record<ProductRecommendationsJsonItemsMappingType, string | number>
  | Record<string, unknown>;

export type ItemPartInfo = string | number | null | undefined;

export type MappedProductData = MappedItem[] | [];

const useProductRecsApi = ({
  mlProductRecsEnabled,
}: {
  mlProductRecsEnabled: boolean | undefined;
}): MappedProductData => {
  const { state } = useCustomComponentStateContext();
  const [mappedItemData, setMappedItemData] = useState<MappedProductData>([]);
  // mlProductRecsEnabled denotes Narvar Product Recommendations A/B testing.
  // If this feature flag is ON and type is ML_ENGINE, we'll use ML Engine based recommendations.
  // Else, we'll fall back to JSON Api based recommendations configured in the same productRecommendations object.
  // api, itemsLookup and itemsLooksMapping fields for ML_ENGINE config
  // will be on jsonApi, jsonItemsLookup and jsonItemsMapping respectively.
  // This is because these properties are optional for ML_ENGINE type and
  // we have to use an alias when fetching these properties from graphql.
  // These aliases can be removed when we are done with the A/B testing.
  const rawJsonData = useJsonApi({ mlProductRecsEnabled });
  const rawCertonaData = useCertonaApi();
  const rawMLProductRecsData = useMLProductRecs({ mlProductRecsEnabled });

  const productRecommendations =
    state?.queryData?.retailerSettings?.productRecommendations;
  let type = productRecommendations?.type;

  let rawProductRecsData: unknown;
  let itemsLookup: string | undefined;
  let itemsMapping:
    | Array<
        Pick<
          ProductRecommendationsJsonItemsMapping,
          'lookup' | 'type' | 'options'
        >
      >
    | undefined;

  if (
    productRecommendations?.type == 'ML_ENGINE' &&
    mlProductRecsEnabled === false
  ) {
    // when feature flag is off, use JSONApi based recommendations.
    type = 'JSON';
    itemsLookup = productRecommendations?.jsonItemLookup;
    itemsMapping = productRecommendations?.jsonItemMapping;
    rawProductRecsData = rawJsonData;
  } else if (productRecommendations?.type === 'ML_ENGINE') {
    itemsLookup = MLProductRecsItemsLookup;
    itemsMapping = MLProductRecsItemsMap;
    rawProductRecsData = rawMLProductRecsData;
  } else if (productRecommendations?.type === 'JSON') {
    itemsLookup = productRecommendations?.itemsLookup;
    itemsMapping = productRecommendations?.itemsMapping;
    rawProductRecsData = rawJsonData;
  } else if (productRecommendations?.type === 'CERTONA') {
    itemsLookup = productRecommendations?.itemsLookup;
    itemsMapping = productRecommendations?.itemsMapping;
    rawProductRecsData = rawCertonaData;
  }

  useEffect(() => {
    if (!rawProductRecsData) return;
    const mustacheContext = new mustache.Context(rawProductRecsData);
    if (typeof itemsLookup !== 'string') return;
    const items: unknown = mustacheContext.lookup(itemsLookup);

    const mappedData = (Array.isArray(items) ? items : []).reduce(
      (mappedItems: MappedProductData, currentItem): MappedProductData | [] => [
        ...mappedItems,
        (Array.isArray(itemsMapping) ? itemsMapping : []).reduce(
          (
            allItemParts: MappedItem,
            { type: partType, lookup: partLookup },
          ): MappedItem => {
            if (!type) return allItemParts;
            const itemContext = new mustache.Context(currentItem);
            const itemPartInfo: unknown = itemContext.lookup(partLookup);

            return {
              ...allItemParts,
              [partType]: itemPartInfo,
            };
          },
          {},
        ),
      ],
      [],
    );

    setMappedItemData(mappedData);
  }, [rawProductRecsData, itemsLookup, itemsMapping, type]);

  return mappedItemData;
};

export default useProductRecsApi;
